import formatShortDate from '@/lib/dates/formatShortDate.js';

const DAY_MS = 24 * 60 * 60 * 1000;

const findLastHistoryEntry = ({ action, status }) => {
  const history = Array.isArray(action?.history) ? action.history : [];

  return [...history].reverse().find((item) => item?.status === status) || null;
};

/*
 * "Dana · 6 days" beats "Dana", because the number is what makes a stalled wait uncomfortable to look at
 * — which is the entire point of naming an owner. Taken from the history entry that set the state rather
 * than a separate stamp, so it cannot disagree with the ledger.
 */
const buildWaitingLabel = ({ action, nowMs }) => {
  const name = String(action?.waitingOn?.name || '').trim();

  if (!name) {
    return null;
  }

  const sinceMs = Date.parse(findLastHistoryEntry({ action, status: 'open' })?.on || '');
  const days = Number.isFinite(sinceMs) ? Math.floor((nowMs - sinceMs) / DAY_MS) : 0;

  return days > 0 ? `${name} · ${days} ${days === 1 ? 'day' : 'days'}` : name;
};

// Who did it, the way the person reading knows them: the ledger's `by` is a user id, `agent` or `portal`.
const toActorLabel = ({ by, currentUserId }) => {
  if (by === 'agent') {
    return 'Pieve';
  }

  if (by === 'portal') {
    return 'the client';
  }

  return by && by === currentUserId ? 'you' : 'a teammate';
};

const STATE_LABELS = Object.freeze({ open: 'Open', done: 'Done', waived: 'Waived' });

/*
 * The actions a person can see, in the order they read them, with the labels the JSX cannot derive on
 * its own. Everything else is the stored action untouched — the rail reads title and status, the modal
 * and the card read the rest — so there is one shape from the database to the screen.
 *
 * Withdrawn is an action taken off the list, and a MindCloud-owned action is our queue: neither is a
 * thing this person can act on, so neither reaches a surface.
 *
 * Open first, then cleared — the cleared ones are kept rather than dropped because "who cleared what, and
 * when" is the question asked at the end of a delivery, which is what `clearedLabel` answers. An open
 * action past its date says Overdue in words, not in colour alone.
 */
const buildWorkflowActionDetails = ({ actions = [], currentUserId = '', nowMs = Date.now() } = {}) => {
  // A due date is a calendar day, so "today" is the viewer's local one, not UTC's.
  const now = new Date(nowMs);
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  return actions
    .filter((action) => action?.key && action?.status !== 'withdrawn' && action?.owner !== 'pieve')
    .map((action) => {
      const isOpen = action.status === 'open';
      const isOverdue = isOpen && Boolean(action.dueBy) && action.dueBy < today;
      const cleared = isOpen ? null : findLastHistoryEntry({ action, status: action.status });
      // Who put it on the list: Pieve (its tool, or a card it mirrored) or a person adding their own.
      const filedBy = String(action.history?.[0]?.by || 'agent');

      return {
        ...action,
        stateLabel: STATE_LABELS[action.status],
        waitingLabel: buildWaitingLabel({ action, nowMs }),
        dueLabel: action.dueBy ? `${isOverdue ? 'Overdue' : 'Due'} ${formatShortDate(action.dueBy)}` : null,
        isOverdue,
        filedByPieve: filedBy === 'agent' || filedBy.startsWith('mirror:'),
        clearedLabel: cleared ? `${STATE_LABELS[action.status]} ${formatShortDate(cleared.on)} by ${toActorLabel({ by: String(cleared.by || ''), currentUserId })}` : null
      };
    })
    .sort((a, b) => Number(b.status === 'open') - Number(a.status === 'open'));
};

export default buildWorkflowActionDetails;
